class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestNumber = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    this.searchTerm = newSearchTerm;
    this.removeAttribute('results');

    this.input.setAttribute('aria-activedescendant', '');

    if (!newSearchTerm.length) {
      this.requestNumber += 1;
      this.close(true);
      return;
    }

    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.requestNumber += 1;

      this.abortController.abort();
      this.abortController = new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(currentSearchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;

      case 'Escape':
        this.requestNumber += 1;
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const options = Array.from(
      this.querySelectorAll(
        'li[role="option"], button.predictive-search__item'
      )
    ).filter((element) => element.offsetParent !== null);

    if (!options.length) return;

    const selected = this.querySelector(
      '[aria-selected="true"]'
    );

    const currentIndex = options.indexOf(selected);
    const moveUp = direction === 'up';

    if (moveUp && currentIndex === -1) return;

    let nextIndex;

    if (moveUp) {
      nextIndex =
        currentIndex <= 0
          ? options.length - 1
          : currentIndex - 1;
    } else {
      nextIndex =
        currentIndex === options.length - 1
          ? 0
          : currentIndex + 1;
    }

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    const activeOption = options[nextIndex];

    activeOption.setAttribute('aria-selected', 'true');

    this.input.setAttribute(
      'aria-activedescendant',
      activeOption.id
    );

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    activeOption.scrollIntoView({
      block: 'nearest',
      inline: 'nearest'
    });
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, ' +
      'button[aria-selected="true"]'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  normalizeSearch(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  getSearchWords(value) {
    return (
      this.normalizeSearch(value).match(
        /[\p{L}\p{N}]+/gu
      ) || []
    );
  }

  async getCatalog() {
    const root = window.Shopify?.routes?.root || '/';

    window.sbPredictiveCatalogs =
      window.sbPredictiveCatalogs || {};

    if (!window.sbPredictiveCatalogs[root]) {
      const loadCatalog = async () => {
        const products = new Map();
        const limit = 250;

        // Límite de seguridad para evitar consultas indefinidas.
        for (let page = 1; page <= 20; page += 1) {
          const response = await fetch(
            `${root}products.json?limit=${limit}&page=${page}`,
            { credentials: 'same-origin' }
          );

          if (!response.ok) {
            throw new Error(
              `Error de catálogo: ${response.status}`
            );
          }

          const data = await response.json();

          if (!Array.isArray(data.products)) {
            throw new Error('Catálogo no válido');
          }

          const previousSize = products.size;

          data.products.forEach((product) => {
            products.set(String(product.id), product);
          });

          if (data.products.length < limit) {
            return Array.from(products.values());
          }

          if (products.size === previousSize) {
            throw new Error(
              'La paginación del catálogo no ha avanzado'
            );
          }
        }

        throw new Error(
          'Catálogo demasiado grande para esta personalización'
        );
      };

      window.sbPredictiveCatalogs[root] =
        loadCatalog().catch((error) => {
          delete window.sbPredictiveCatalogs[root];
          throw error;
        });
    }

    return window.sbPredictiveCatalogs[root];
  }

  createProductItem(product, index, template) {
    const item = template
      ? template.cloneNode(true)
      : document.createElement('li');

    // Eliminar identificadores heredados del producto clonado.
    item.removeAttribute('id');

    item.querySelectorAll('[id]').forEach((element) => {
      element.removeAttribute('id');
    });

    item.id = `predictive-search-option-extra-${index}`;
    item.className = 'predictive-search__list-item';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    const root = window.Shopify?.routes?.root || '/';

    const link = document.createElement('a');
    link.className =
      'predictive-search__item ' +
      'predictive-search__item--link-with-thumbnail ' +
      'link link--text';

    link.href =
      `${root}products/${encodeURIComponent(product.handle)}`;

    link.tabIndex = -1;

    const imageData = product.images?.[0];

    if (imageData?.src) {
      const image = document.createElement('img');

      image.className = 'predictive-search__image';
      image.src = imageData.src;
      image.alt = imageData.alt || product.title;
      image.width = 50;
      image.height = 50;
      image.decoding = 'async';
      image.style.objectFit = 'contain';

      link.appendChild(image);
    }

    const content = document.createElement('div');
    content.className =
      'predictive-search__item-content ' +
      'predictive-search__item-content--centered';

    const title = document.createElement('p');
    title.className = 'predictive-search__item-heading h5';
    title.textContent = product.title;

    content.appendChild(title);
    link.appendChild(content);

    item.replaceChildren(link);

    return item;
  }

  getProductHandle(link) {
    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)/);

      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  supplementProducts(section, catalog, searchTerm) {
    const terms = this.getSearchWords(searchTerm);

    if (!terms.length) return;

    const matchingProducts = catalog.filter((product) => {
      const words = this.getSearchWords(product.title);

      return terms.every((term) =>
        words.some((word) => word.startsWith(term))
      );
    });

    if (!matchingProducts.length) return;

    let list = section.querySelector(
      '#predictive-search-results-products-list'
    );

    if (!list) {
      const results = section.querySelector(
        '#predictive-search-results'
      );

      if (!results) return;

      const group = document.createElement('div');
      group.className = 'predictive-search__result-group';

      list = document.createElement('ul');
      list.id = 'predictive-search-results-products-list';
      list.className =
        'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');

      const label = this.input.getAttribute('aria-label');

      if (label) {
        list.setAttribute('aria-label', label);
      }

      group.appendChild(list);

      const groups = section.querySelector(
        '#predictive-search-results-groups-wrapper'
      );

      if (groups) {
        groups.appendChild(group);
      } else {
        results.prepend(group);
      }
    }

    const originalItems = Array.from(list.children);
    const existingByHandle = new Map();

    originalItems.forEach((item) => {
      const link = item.querySelector('a[href]');

      if (link) {
        const handle = this.getProductHandle(link);

        if (handle) existingByHandle.set(handle, item);
      }
    });

    const output = [];
    const usedHandles = new Set();
    const template = originalItems[0] || null;

    // Primero las coincidencias por nombre.
    matchingProducts.forEach((product, index) => {
      if (usedHandles.has(product.handle)) return;

      const item =
        existingByHandle.get(product.handle) ||
        this.createProductItem(product, index, template);

      output.push(item);
      usedHandles.add(product.handle);
    });

    // Después, los demás resultados originales.
    originalItems.forEach((item) => {
      const link = item.querySelector('a[href]');
      const handle = link ? this.getProductHandle(link) : '';

      if (!handle || !usedHandles.has(handle)) {
        output.push(item);

        if (handle) usedHandles.add(handle);
      }
    });

    list.replaceChildren(...output.slice(0, 10));

    const groups = section.querySelector(
      '#predictive-search-results-groups-wrapper'
    );

    groups?.classList.remove(
      'predictive-search__results-groups-wrapper--no-products'
    );

    const liveCount = section.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (liveCount) {
      const productTitles = Array.from(
        list.querySelectorAll(
          '.predictive-search__item-heading'
        )
      ).map((element) => element.textContent.trim());

      liveCount.textContent = productTitles.join(', ');
    }
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    const requestNumber = ++this.requestNumber;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      // Si falla el catálogo adicional, se conserva
      // la búsqueda original de Shopify.
      const catalogPromise = this.getCatalog().catch((error) => {
        console.warn(
          'No se han añadido coincidencias por nombre:',
          error
        );

        return null;
      });

      const response = await fetch(
        `${routes.predictive_search_url}` +
        `?q=${encodeURIComponent(searchTerm)}` +
        '&section_id=predictive-search',
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        throw new Error(
          `Error de búsqueda: ${response.status}`
        );
      }

      const text = await response.text();

      const documentResult = new DOMParser().parseFromString(
        text,
        'text/html'
      );

      const section = documentResult.querySelector(
        '#shopify-section-predictive-search'
      );

      if (!section) {
        throw new Error(
          'No se ha encontrado la sección del buscador'
        );
      }

      // Mostrar primero los resultados nativos sin esperar
      // a que termine la descarga inicial del catálogo.
      if (
        requestNumber !== this.requestNumber ||
        this.getQuery() !== searchTerm
      ) {
        return;
      }

      this.renderSearchResults(section.innerHTML);

      const catalog = await catalogPromise;

      if (
        requestNumber !== this.requestNumber ||
        this.getQuery() !== searchTerm
      ) {
        return;
      }

      if (catalog) {
        this.supplementProducts(
          section,
          catalog,
          searchTerm
        );
      }

      const resultsMarkup = section.innerHTML;

      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultsMarkup;
      });

      // No reabrir si el cliente ya salió del buscador.
      if (!this.contains(document.activeElement)) return;

      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        requestNumber !== this.requestNumber
      ) {
        return;
      }

      this.removeAttribute('loading');
      this.close();

      console.error('Error del buscador:', error);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text') ||
      '';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    clearTimeout(this.statusTimeout);

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    this.statusTimeout = setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const count = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    this.setLiveRegionText(count?.textContent || '');
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector('.section-header')
        ?.getBoundingClientRect().bottom || 0;

    const isMobile = window.innerWidth <= 749;
    const margin = isMobile ? 80 : 20;

    this.resultsMaxHeight = Math.max(
      120,
      (window.innerHeight - headerBottom - margin) * 0.8
    );

    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`;

    this.predictiveSearchResults.style.overflowY = 'auto';

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector(
      '[aria-selected="true"]'
    );

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', false);

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
